/**
 * OrganizationCTACard Component
 * Displays and manages a single Organization CTA with QR code generation
 */

import React, { useState, useEffect } from "react";
import {
  QrCodeIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  PlusIcon,
  XMarkIcon,
  ArrowPathIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import {
  useUpdateOrganizationCTA,
  useDeleteOrganizationCTA,
  useGenerateOrganizationCTAQRCode,
  useOffersForOrganization,
} from "../../hooks/useOrganizationCTAs";
import { buildOrganizationCTAUrl } from "../../services/organizationCTA.service";
import CtaActivityTypeSelect from "./CtaActivityTypeSelect";
import {
  ctaActivityLabel,
  ctaActivityToColumn,
} from "../../utils/ctaActivityType";
import type { Organization } from "../../types/organization.types";
import type {
  OrganizationCTA,
  OrganizationCTARewardType,
} from "../../types/organizationCTA.types";
import { SUBSCRIPTION_CTA_BASE_URL } from "../../types/organizationCTA.types";
import Card from "../ui/Card";
import Button from "../common/Button";
import LoadingSpinner from "../common/LoadingSpinner";
import { useToast } from "../ui/Toast";
import ConfirmDialog from "../ui/ConfirmDialog";
import PromoCodeSelect from "./PromoCodeSelect";
import PromoCodeLookupStatus from "../common/PromoCodeLookupStatus";

interface OrganizationCTACardProps {
  cta: OrganizationCTA;
  organization: Organization;
  onUpdate: () => void;
  /** false = read-only (copy link / download QR only); see can_manage_organization_ctas(). */
  canEdit: boolean;
}

// Subscription CTA field definitions
const SUBSCRIPTION_FIELDS = [
  {
    key: "affid",
    label: "Promoter Affiliate ID",
    placeholder: "e.g., ABC123",
  },
  { key: "promo", label: "Promo Code", placeholder: "e.g., SUMMER2025" },
  { key: "altprice", label: "Alt Price", placeholder: "e.g., 10.99" },
  { key: "utm_source", label: "UTM Source", placeholder: "e.g., event" },
  { key: "utm_term", label: "UTM Term", placeholder: "e.g., signup" },
  {
    key: "utm_campaign",
    label: "UTM Campaign",
    placeholder: "e.g., spring_promo",
  },
  { key: "utm_medium", label: "UTM Medium", placeholder: "e.g., qrcode" },
  { key: "utm_content", label: "UTM Content", placeholder: "e.g., banner_ad" },
];

const OrganizationCTACard: React.FC<OrganizationCTACardProps> = ({
  cta,
  organization,
  onUpdate,
  canEdit,
}) => {
  const { showToast } = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);

  // Form state
  const [name, setName] = useState(cta.name);
  const [baseUrl, setBaseUrl] = useState(cta.base_url || "");
  const [parameters, setParameters] = useState<Record<string, string>>(
    cta.parameters,
  );

  // Reward state
  const [rewardType, setRewardType] = useState<OrganizationCTARewardType>(
    cta.reward_type || "none",
  );
  const [selectedOfferId, setSelectedOfferId] = useState<string>(
    cta.offer_id || "",
  );

  // Voucher state
  const [voucherAmount, setVoucherAmount] = useState(
    cta.voucher_amount?.toString() || "",
  );
  const [voucherExpiryDays, setVoucherExpiryDays] = useState(
    cta.voucher_expiry_days?.toString() || "30",
  );
  const [voucherStartDate, setVoucherStartDate] = useState(
    cta.voucher_start_date || "",
  );
  const [voucherEndDate, setVoucherEndDate] = useState(
    cta.voucher_end_date || "",
  );

  // URL configuration
  const [includeStateParam, setIncludeStateParam] = useState(
    cta.include_state_param !== false,
  );
  // `=== true`, not `!== false`: this flag defaults OFF, so a CTA saved before it existed
  // (use_state_defaults absent) must read as unchecked.
  const [useStateDefaults, setUseStateDefaults] = useState(
    cta.use_state_defaults === true,
  );
  // Off by default (owner 2026-09-29); `=== true` so a CTA saved before this option existed reads unchecked.
  const [singleVoucher, setSingleVoucher] = useState(
    cta.single_voucher === true,
  );
  // `=== true` not `!== false`: unlike include_state_param this defaults OFF, so an
  // undefined value (an older row read before the column existed) must read as off.
  const [allowVariableVoucherValue, setAllowVariableVoucherValue] = useState(
    cta.allow_variable_voucher_value === true,
  );
  const [useMemberSignup, setUseMemberSignup] = useState(
    cta.use_member_signup || false,
  );
  const [displayAwayPlans, setDisplayAwayPlans] = useState(
    cta.display_away_plans || false,
  );

  // For 'other' type: dynamic parameter management
  // MSR EVT-04: the event activity this CTA is for ("" = any activity).
  const [activityType, setActivityType] = useState<string>(
    cta.activity_type ?? "",
  );
  const [newParamKey, setNewParamKey] = useState("");
  const [newParamValue, setNewParamValue] = useState("");

  // Fetch available offers
  const { data: availableOffers, isLoading: isLoadingOffers } =
    useOffersForOrganization(organization.id);

  // Mutations
  const updateCTA = useUpdateOrganizationCTA();
  const deleteCTA = useDeleteOrganizationCTA();
  const generateQR = useGenerateOrganizationCTAQRCode();

  // Reset form when CTA changes
  useEffect(() => {
    setName(cta.name);
    setBaseUrl(cta.base_url || "");
    setParameters(cta.parameters);
    setRewardType(cta.reward_type || "none");
    setSelectedOfferId(cta.offer_id || "");
    setVoucherAmount(cta.voucher_amount?.toString() || "");
    setVoucherExpiryDays(cta.voucher_expiry_days?.toString() || "30");
    setVoucherStartDate(cta.voucher_start_date || "");
    setVoucherEndDate(cta.voucher_end_date || "");
    setIncludeStateParam(cta.include_state_param !== false);
    setUseStateDefaults(cta.use_state_defaults === true);
    setSingleVoucher(cta.single_voucher === true);
    setAllowVariableVoucherValue(cta.allow_variable_voucher_value === true);
    setUseMemberSignup(cta.use_member_signup || false);
    setDisplayAwayPlans(cta.display_away_plans || false);
    setActivityType(cta.activity_type ?? "");
  }, [cta]);

  // Build preview URL
  const previewUrl = buildOrganizationCTAUrl(
    {
      id: cta.id,
      cta_type: cta.cta_type,
      base_url: baseUrl,
      parameters,
      // Pass the CTA's location so `ste` uses the location's state (for
      // location-tied CTAs), not only the org's address_state.
      location: cta.location,
      offer: selectedOfferId
        ? availableOffers?.find((o) => o.id === selectedOfferId)
        : cta.offer,
      voucher_amount: voucherAmount
        ? parseFloat(voucherAmount)
        : cta.voucher_amount,
      include_state_param: includeStateParam,
      use_state_defaults: useStateDefaults,
      single_voucher: singleVoucher,
      allow_variable_voucher_value:
        rewardType === "voucher" && allowVariableVoucherValue,
      use_member_signup: useMemberSignup,
      display_away_plans: displayAwayPlans,
    },
    organization,
  );

  const handleSave = async () => {
    if (!name.trim()) {
      showToast("error", "Please enter a name");
      return;
    }

    if (cta.cta_type === "other" && !baseUrl.trim()) {
      showToast("error", "Please enter a base URL");
      return;
    }

    // Validate voucher amount if reward type is voucher
    if (
      rewardType === "voucher" &&
      (!voucherAmount || parseFloat(voucherAmount) <= 0)
    ) {
      showToast("error", "Please enter a valid voucher amount");
      return;
    }

    // Validate offer selection if reward type is offer
    if (rewardType === "offer" && !selectedOfferId) {
      showToast("error", "Please select an offer");
      return;
    }

    // Computed once: the payload and the "did it change?" test below must never disagree.
    const savedAllowVariable =
      cta.cta_type === "subscription" && rewardType === "voucher"
        ? allowVariableVoucherValue
        : false;

    try {
      await updateCTA.mutateAsync({
        ctaId: cta.id,
        updates: {
          name: name.trim(),
          base_url:
            cta.cta_type === "subscription"
              ? SUBSCRIPTION_CTA_BASE_URL
              : baseUrl.trim(),
          parameters,
          reward_type: rewardType,
          offer_id: rewardType === "offer" ? selectedOfferId : null,
          voucher_amount:
            rewardType === "voucher" && voucherAmount
              ? parseFloat(voucherAmount)
              : null,
          voucher_expiry_days:
            rewardType === "voucher" && voucherExpiryDays
              ? parseInt(voucherExpiryDays, 10)
              : undefined,
          voucher_start_date:
            rewardType === "voucher" && voucherStartDate
              ? voucherStartDate
              : null,
          voucher_end_date:
            rewardType === "voucher" && voucherEndDate ? voucherEndDate : null,
          include_state_param:
            cta.cta_type === "subscription" ? includeStateParam : undefined,
          use_state_defaults:
            cta.cta_type === "subscription" ? useStateDefaults : undefined,
          single_voucher:
            cta.cta_type === "subscription" ? singleVoucher : undefined,
          allow_variable_voucher_value: savedAllowVariable,
          use_member_signup:
            cta.cta_type === "subscription" ? useMemberSignup : undefined,
          display_away_plans:
            cta.cta_type === "subscription" ? displayAwayPlans : undefined,
          // MSR EVT-04: sent only when changed, so saving any other field never touches it.
          ...(cta.cta_type === "subscription" &&
          ctaActivityToColumn(activityType) !== (cta.activity_type ?? null)
            ? { activity_type: ctaActivityToColumn(activityType) }
            : {}),
        },
      });
      // The stored generated_url — not the live preview — is what Copy hands out and what
      // the QR encodes. Every other edited field leaves it stale until someone presses
      // "Regenerate URL & QR", but for THIS flag a stale URL means the tick did nothing at
      // all: discountvalue would still be in the link. So refresh it here, and only here.
      // Already-printed codes are unaffected; they encode the old URL, which still works.
      // singlevoucher=1 is the same kind of flag: it exists only to change the URL/QR, so a
      // save that toggles it refreshes them too (owner 2026-09-29).
      if (
        cta.cta_type === "subscription" &&
        (savedAllowVariable !== (cta.allow_variable_voucher_value === true) ||
          singleVoucher !== (cta.single_voucher === true)) &&
        cta.generated_url
      ) {
        try {
          await generateQR.mutateAsync({ ctaId: cta.id, organization });
          showToast("success", "CTA updated — URL and QR regenerated");
        } catch {
          showToast(
            "error",
            "Saved, but the URL and QR still carry the old value. Press Regenerate URL & QR.",
          );
        }
      } else {
        showToast("success", "CTA updated");
      }
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update CTA";
      showToast("error", message);
    }
  };

  const handleCancel = () => {
    setName(cta.name);
    setBaseUrl(cta.base_url || "");
    setParameters(cta.parameters);
    setRewardType(cta.reward_type || "none");
    setSelectedOfferId(cta.offer_id || "");
    setVoucherAmount(cta.voucher_amount?.toString() || "");
    setVoucherExpiryDays(cta.voucher_expiry_days?.toString() || "30");
    setVoucherStartDate(cta.voucher_start_date || "");
    setVoucherEndDate(cta.voucher_end_date || "");
    setIncludeStateParam(cta.include_state_param !== false);
    setUseStateDefaults(cta.use_state_defaults === true);
    setSingleVoucher(cta.single_voucher === true);
    setAllowVariableVoucherValue(cta.allow_variable_voucher_value === true);
    setUseMemberSignup(cta.use_member_signup || false);
    setDisplayAwayPlans(cta.display_away_plans || false);
    setActivityType(cta.activity_type ?? "");
    setIsEditing(false);
  };

  const handleToggleActive = async () => {
    try {
      await updateCTA.mutateAsync({
        ctaId: cta.id,
        updates: { is_active: !cta.is_active },
      });
      showToast("success", cta.is_active ? "CTA deactivated" : "CTA activated");
      setShowDeactivateConfirm(false);
      onUpdate();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update CTA";
      showToast("error", message);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCTA.mutateAsync({
        ctaId: cta.id,
        organizationId: cta.organization_id,
      });
      showToast("success", "CTA deleted");
      setShowDeleteConfirm(false);
      onUpdate();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to delete CTA";
      showToast("error", message);
    }
  };

  const handleGenerateQR = async () => {
    // Validate that we have enough parameters
    if (cta.cta_type === "subscription" && !organization.address_state) {
      showToast("error", "Organization must have a state set");
      return;
    }

    try {
      await generateQR.mutateAsync({ ctaId: cta.id, organization });
      showToast("success", "QR code generated");
      onUpdate();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to generate QR code";
      showToast("error", message);
    }
  };

  const handleCopyUrl = async () => {
    const urlToCopy = cta.generated_url || previewUrl;
    try {
      await navigator.clipboard.writeText(urlToCopy);
      showToast("success", "URL copied to clipboard");
    } catch {
      showToast("error", "Failed to copy URL");
    }
  };

  const handleDownloadQR = async () => {
    if (!cta.qr_code_url) {
      return;
    }

    try {
      // Fetch the QR code image
      const response = await fetch(cta.qr_code_url);
      const blob = await response.blob();

      // Filename: {org name}.png. Per 2026-05-18 operator feedback,
      // dropped cta.name to keep internal CTA labels out of user-facing
      // download filenames (event-CTA side had the same change).
      const orgName = (organization.name || "organization").replace(
        /[^a-zA-Z0-9]+/g,
        "-",
      );
      const filename = `${orgName}.png`;

      // If it's an SVG, convert to PNG
      if (blob.type === "image/svg+xml" || cta.qr_code_url.endsWith(".svg")) {
        const svgText = await blob.text();
        const img = new Image();
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        await new Promise<void>((resolve, reject) => {
          img.onload = () => {
            // Set canvas size (use larger size for better quality)
            canvas.width = 512;
            canvas.height = 512;
            if (ctx) {
              // White background
              ctx.fillStyle = "white";
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            }
            resolve();
          };
          img.onerror = reject;
          // UTF-8-safe: btoa() only handles Latin-1 and throws on any code
          // point > 255 (e.g. the "…" ellipsis truncate() adds for labels
          // longer than 32 chars, or smart quotes/accents in org/rep names).
          // That broke QR download for every org with a long name. Use a
          // charset=utf-8 data URI instead of base64.
          img.src =
            "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgText);
        });

        canvas.toBlob((pngBlob) => {
          if (pngBlob) {
            const url = URL.createObjectURL(pngBlob);
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }
        }, "image/png");
      } else {
        // Already PNG or other format, download directly
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Failed to download QR code:", error);
      showToast("error", "Failed to download QR code");
    }
  };

  // Add a new parameter for 'other' type
  const handleAddParameter = () => {
    if (!newParamKey.trim()) {
      showToast("error", "Enter a parameter name");
      return;
    }
    setParameters((prev) => ({
      ...prev,
      [newParamKey.trim()]: newParamValue,
    }));
    setNewParamKey("");
    setNewParamValue("");
  };

  // Remove a parameter
  const handleRemoveParameter = (key: string) => {
    setParameters((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  // Update a parameter value
  const handleParameterChange = (key: string, value: string) => {
    setParameters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <>
      <Card
        variant="outlined"
        padding="none"
        className={`overflow-hidden${!cta.is_active ? " opacity-60" : ""}`}
      >
        {/* Header */}
        <div
          className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center space-x-3">
            <QrCodeIcon className="h-5 w-5 text-gray-400" />
            <div>
              <h4 className="font-medium text-gray-900">{cta.name}</h4>
              <p className="text-xs text-gray-500">
                {cta.cta_type === "subscription"
                  ? `Subscription CTA · For: ${ctaActivityLabel(cta.activity_type)}`
                  : "Custom CTA"}
              </p>
              {cta.location && (
                <p className="text-xs text-gray-400 flex items-center mt-0.5">
                  <MapPinIcon className="h-3 w-3 mr-1 inline" />
                  {cta.location.name}
                  {cta.location.city ? ` — ${cta.location.city}` : ""}
                  {cta.location.state ? `, ${cta.location.state}` : ""}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {!cta.is_active && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                Inactive
              </span>
            )}
            {cta.reward_type === "voucher" && cta.voucher_amount && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                Voucher: ${cta.voucher_amount}
              </span>
            )}
            {cta.reward_type === "offer" && cta.offer && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                Offer: {cta.offer.name}
                {cta.offer.value_amount && ` ($${cta.offer.value_amount})`}
              </span>
            )}
            {cta.qr_code_url && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                QR Ready
              </span>
            )}
            {isExpanded ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="p-4 space-y-4">
            {/* Name Field */}
            {isEditing ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            ) : null}

            {/* MSR EVT-04: which event activity this CTA is for */}
            {isEditing && cta.cta_type === "subscription" && (
              <CtaActivityTypeSelect
                id={`cta-activity-type-${cta.id}`}
                value={activityType}
                onChange={setActivityType}
                current={cta.activity_type}
              />
            )}

            {/* Parameters Section */}
            {cta.cta_type === "subscription" ? (
              // Subscription CTA Fields
              <div className="space-y-3">
                <h5 className="text-sm font-medium text-gray-700">
                  Parameters
                </h5>

                {/* Auto-populated fields info */}
                <div className="bg-blue-50 rounded-md p-3 text-sm text-blue-700">
                  <p className="font-medium mb-1">Auto-populated:</p>
                  <ul className="list-disc list-inside text-xs space-y-0.5">
                    <li
                      className={
                        !includeStateParam ? "line-through opacity-50" : ""
                      }
                    >
                      <code className="bg-blue-100 px-1 rounded">ste</code>:{" "}
                      {organization.address_state || "(not set)"}
                    </li>
                    <li
                      className={
                        rewardType === "voucher" && allowVariableVoucherValue
                          ? "line-through opacity-50"
                          : ""
                      }
                    >
                      <code className="bg-blue-100 px-1 rounded">
                        discountvalue
                      </code>
                      :{" "}
                      {rewardType === "voucher" && allowVariableVoucherValue
                        ? "(not in the URL \u2014 looked up by cta-voucher-value)"
                        : voucherAmount
                          ? voucherAmount
                          : cta.voucher_amount
                            ? cta.voucher_amount
                            : cta.offer?.value_amount
                              ? cta.offer.value_amount
                              : "(set voucher amount or select offer)"}
                    </li>
                  </ul>
                </div>

                {/* Include state parameter checkbox */}
                {isEditing && (
                  <>
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeStateParam}
                        onChange={(e) => setIncludeStateParam(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      Include state parameter (
                      <code className="text-xs bg-gray-100 px-1 rounded">
                        ste=
                      </code>
                      ) in URL
                    </label>
                    <label className="flex items-start gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={useStateDefaults}
                        onChange={(e) => setUseStateDefaults(e.target.checked)}
                        className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>
                        Use State Defaults
                        <span className="block text-xs text-gray-500">
                          If the member picks a different state during signup,
                          the voucher amount switches to that state's default.{" "}
                          Adds{" "}
                          <code className="bg-gray-100 px-1 rounded">
                            usestatedefaults=1
                          </code>{" "}
                          to the URL. Off by default, so existing CTAs and
                          printed QR codes are unchanged.
                        </span>
                      </span>
                    </label>

                    {(rewardType === "voucher" || rewardType === "offer") && (
                      <label className="flex items-start gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          aria-label="Single voucher"
                          checked={singleVoucher}
                          onChange={(e) => setSingleVoucher(e.target.checked)}
                          className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span>
                          Single voucher
                          <span className="block text-xs text-gray-500">
                            Creates the voucher for the primary member only, not
                            the secondary. Adds{" "}
                            <code className="bg-gray-100 px-1 rounded">
                              singlevoucher=1
                            </code>{" "}
                            to the QR code and landing page URL. Off by default.
                          </span>
                        </span>
                      </label>
                    )}

                    {/* Use member signup URL checkbox */}
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useMemberSignup}
                        onChange={(e) => setUseMemberSignup(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      Use member signup URL
                      <span className="text-xs text-gray-500">
                        Skips the voucher landing page and starts on plan
                        selection (unchecked = /voucher/, checked =
                        /member-signup/)
                      </span>
                    </label>

                    {/* Variable voucher value. Editing an EXISTING CTA is where this is
                        most dangerous — the QR code may already be printed and in the
                        field — so the warning is the same one the create form shows. */}
                    {rewardType === "voucher" && (
                      <div>
                        <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={allowVariableVoucherValue}
                            onChange={(e) =>
                              setAllowVariableVoucherValue(e.target.checked)
                            }
                            className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span>
                            Allow this voucher value to change without requiring
                            a new QR Code
                            <span className="block text-xs text-gray-500">
                              Leaves{" "}
                              <code className="bg-gray-100 px-1 rounded">
                                discountvalue
                              </code>{" "}
                              out of the URL so the amount can change without
                              reprinting. The landing page looks up the current
                              amount when the code is scanned.
                            </span>
                          </span>
                        </label>
                        {allowVariableVoucherValue && (
                          <div className="mt-2 ml-6 bg-blue-50 border border-blue-200 rounded-md p-2">
                            <p className="text-xs text-blue-800">
                              The amount is not in the link. When the code is
                              scanned, ww2.uslawshield.com looks up this
                              CTA&rsquo;s current voucher amount, so a change
                              here also applies to QR codes already printed.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Display away plans checkbox */}
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={displayAwayPlans}
                        onChange={(e) => setDisplayAwayPlans(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      Display away plans
                      <span className="text-xs text-gray-500">
                        (with member signup URL, starts on the Away plans
                        screen; adds plantype=away)
                      </span>
                    </label>
                  </>
                )}

                {/* Editable fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {SUBSCRIPTION_FIELDS.map((field) =>
                    // Editing an existing CTA picks from the organization's codes. Migration
                    // 2026-09-16-024 guarantees this CTA's current code is among them, so opening
                    // the picker can never silently change what an existing link sends.
                    field.key === "promo" && isEditing ? (
                      <div key={field.key}>
                        <PromoCodeSelect
                          organizationId={cta.organization_id}
                          value={parameters[field.key] || ""}
                          onChange={(code) =>
                            handleParameterChange("promo", code)
                          }
                          helpText="Add more codes on the Promo Codes tab to see them here."
                        />
                        {/* Informational only -- never blocks the save (owner rule 2026-10-25). */}
                        <PromoCodeLookupStatus
                          code={parameters[field.key]}
                          canRecheck={canEdit}
                        />
                      </div>
                    ) : (
                      <div key={field.key}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {field.label}
                        </label>
                        <input
                          type="text"
                          value={parameters[field.key] || ""}
                          onChange={(e) =>
                            handleParameterChange(field.key, e.target.value)
                          }
                          placeholder={field.placeholder}
                          disabled={!isEditing}
                          className={`
                            w-full px-3 py-2 border rounded-md text-sm
                            ${
                              isEditing
                                ? "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                                : "border-gray-200 bg-gray-50 text-gray-600"
                            }
                          `}
                        />
                        {field.key === "promo" && (
                          <PromoCodeLookupStatus
                            code={parameters.promo}
                            canRecheck={canEdit}
                          />
                        )}
                      </div>
                    ),
                  )}
                </div>
              </div>
            ) : (
              // Other CTA Fields
              <div className="space-y-3">
                {/* Base URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Base URL
                  </label>
                  <input
                    type="url"
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                    placeholder="https://example.com/landing"
                    disabled={!isEditing}
                    className={`
                      w-full px-3 py-2 border rounded-md
                      ${
                        isEditing
                          ? "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                          : "border-gray-200 bg-gray-50 text-gray-600"
                      }
                    `}
                  />
                </div>

                {/* Dynamic Parameters */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL Parameters
                  </label>

                  {Object.keys(parameters).length > 0 ? (
                    <div className="space-y-2 mb-3">
                      {Object.entries(parameters).map(([key, value]) => (
                        <div key={key} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={key}
                            disabled
                            className="w-1/3 px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm"
                          />
                          <span className="text-gray-400">=</span>
                          <input
                            type="text"
                            value={value}
                            onChange={(e) =>
                              handleParameterChange(key, e.target.value)
                            }
                            disabled={!isEditing}
                            className={`
                              flex-1 px-3 py-2 border rounded-md text-sm
                              ${
                                isEditing
                                  ? "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                                  : "border-gray-200 bg-gray-50"
                              }
                            `}
                          />
                          {isEditing && (
                            <button
                              type="button"
                              onClick={() => handleRemoveParameter(key)}
                              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 mb-3">
                      No parameters added yet
                    </p>
                  )}

                  {/* Add new parameter */}
                  {isEditing && (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={newParamKey}
                        onChange={(e) => setNewParamKey(e.target.value)}
                        placeholder="param_name"
                        className="w-1/3 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                      />
                      <span className="text-gray-400">=</span>
                      <input
                        type="text"
                        value={newParamValue}
                        onChange={(e) => setNewParamValue(e.target.value)}
                        placeholder="value"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                      />
                      <button
                        type="button"
                        onClick={handleAddParameter}
                        className="p-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded"
                      >
                        <PlusIcon className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Reward Configuration: None, Voucher, or Offer */}
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <h5 className="text-sm font-medium text-gray-700">
                Reward Configuration
              </h5>

              {isEditing ? (
                <>
                  {/* Reward Type Selector (editing mode) */}
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setRewardType("none");
                        setSelectedOfferId("");
                        setVoucherAmount("");
                        setVoucherStartDate("");
                        setVoucherEndDate("");
                      }}
                      className={`
                        p-2 border rounded text-center text-sm transition-colors
                        ${
                          rewardType === "none"
                            ? "border-primary-500 bg-primary-50 ring-1 ring-primary-500"
                            : "border-gray-300 hover:border-gray-400"
                        }
                      `}
                    >
                      None
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setRewardType("voucher");
                        setSelectedOfferId("");
                      }}
                      className={`
                        p-2 border rounded text-center text-sm transition-colors
                        ${
                          rewardType === "voucher"
                            ? "border-primary-500 bg-primary-50 ring-1 ring-primary-500"
                            : "border-gray-300 hover:border-gray-400"
                        }
                      `}
                    >
                      Voucher
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setRewardType("offer");
                        setVoucherAmount("");
                        setVoucherStartDate("");
                        setVoucherEndDate("");
                      }}
                      className={`
                        p-2 border rounded text-center text-sm transition-colors
                        ${
                          rewardType === "offer"
                            ? "border-primary-500 bg-primary-50 ring-1 ring-primary-500"
                            : "border-gray-300 hover:border-gray-400"
                        }
                      `}
                    >
                      Offer
                    </button>
                  </div>

                  {/* Voucher Configuration (editing mode) */}
                  {rewardType === "voucher" && (
                    <div className="bg-green-50 border border-green-200 rounded p-3 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Amount ($) <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={voucherAmount}
                            onChange={(e) => setVoucherAmount(e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Expiration (Days){" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={voucherExpiryDays}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\./g, "");
                              setVoucherExpiryDays(val);
                            }}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Days after creation until expiry
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Valid From
                          </label>
                          <input
                            type="date"
                            value={voucherStartDate}
                            onChange={(e) =>
                              setVoucherStartDate(e.target.value)
                            }
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Valid Until
                          </label>
                          <input
                            type="date"
                            value={voucherEndDate}
                            onChange={(e) => setVoucherEndDate(e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Offer Selection (editing mode) */}
                  {rewardType === "offer" && (
                    <div className="bg-green-50 border border-green-200 rounded p-3">
                      {isLoadingOffers ? (
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <LoadingSpinner size="small" />
                          <span>Loading offers...</span>
                        </div>
                      ) : availableOffers && availableOffers.length > 0 ? (
                        <select
                          value={selectedOfferId}
                          onChange={(e) => setSelectedOfferId(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="">Select an offer...</option>
                          {availableOffers.map((offer) => (
                            <option key={offer.id} value={offer.id}>
                              {offer.name} ({offer.offer_code})
                              {offer.value_amount
                                ? ` - $${offer.value_amount}`
                                : ""}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="text-sm text-gray-600">
                          No offers available for this organization.
                        </p>
                      )}
                    </div>
                  )}
                </>
              ) : (
                /* View mode */
                <div className="text-sm">
                  {cta.reward_type === "none" || !cta.reward_type ? (
                    <p className="text-gray-500">
                      No reward configured (tracking only)
                    </p>
                  ) : cta.reward_type === "voucher" ? (
                    <div className="space-y-1">
                      <p className="text-green-700 font-medium">
                        Voucher: ${cta.voucher_amount?.toFixed(2)}
                      </p>
                      <p className="text-gray-500 text-xs">
                        Expires {cta.voucher_expiry_days} days after creation
                      </p>
                      {(cta.voucher_start_date || cta.voucher_end_date) && (
                        <p className="text-gray-500 text-xs">
                          Valid: {cta.voucher_start_date || "Any"} →{" "}
                          {cta.voucher_end_date || "No expiry"}
                        </p>
                      )}
                    </div>
                  ) : cta.reward_type === "offer" && cta.offer ? (
                    <div className="space-y-1">
                      <p className="text-green-700 font-medium">
                        Offer: {cta.offer.name}
                      </p>
                      <p className="text-gray-500 text-xs">
                        Code: {cta.offer.offer_code}
                        {cta.offer.value_amount && (
                          <span className="ml-2">
                            Value: ${cta.offer.value_amount}
                          </span>
                        )}
                      </p>
                      {cta.offer.description && (
                        <p className="text-gray-500 text-xs">
                          {cta.offer.description}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-500">No offer selected</p>
                  )}
                </div>
              )}
            </div>

            {/* Generated URL Preview */}
            <div className="bg-gray-50 rounded-md p-3">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-500 uppercase">
                  {cta.generated_url ? "Generated URL" : "URL Preview"}
                </label>
                <button
                  type="button"
                  onClick={handleCopyUrl}
                  className="text-xs text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                >
                  <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                  <span>Copy</span>
                </button>
              </div>
              <p className="text-sm text-gray-700 break-all font-mono">
                {cta.generated_url || previewUrl}
              </p>
            </div>

            {/* QR Code Section */}
            {cta.qr_code_url && (
              <div className="flex items-start space-x-4 p-4 bg-white border border-gray-200 rounded-lg">
                <div className="bg-white p-2 border border-gray-200 rounded-lg">
                  <img
                    src={cta.qr_code_url}
                    alt={`QR code for ${cta.name}`}
                    className="w-40 max-h-48 object-contain"
                  />
                </div>
                <div className="flex-1">
                  <h5 className="font-medium text-gray-900 mb-1">QR Code</h5>
                  <p className="text-sm text-gray-500 mb-3">
                    Scan to open the generated URL
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={ArrowDownTrayIcon}
                    onClick={handleDownloadQR}
                  >
                    Download PNG
                  </Button>
                </div>
              </div>
            )}

            {/* Action Buttons — write actions only for can_manage_organization_ctas() roles */}
            {canEdit && (
              <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                <div>
                  {isEditing ? (
                    <div className="flex space-x-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSave}
                        disabled={updateCTA.isPending}
                      >
                        {updateCTA.isPending ? "Saving..." : "Save Changes"}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setIsEditing(true)}
                    >
                      Edit Parameters
                    </Button>
                  )}
                </div>

                <div className="flex space-x-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={cta.qr_code_url ? ArrowPathIcon : QrCodeIcon}
                    onClick={handleGenerateQR}
                    disabled={generateQR.isPending}
                  >
                    {generateQR.isPending
                      ? "Generating..."
                      : cta.qr_code_url
                        ? "Regenerate URL & QR"
                        : "Generate URL & QR"}
                  </Button>
                  <Button
                    variant={cta.is_active ? "secondary" : "primary"}
                    size="sm"
                    onClick={() => {
                      if (cta.is_active) {
                        setShowDeactivateConfirm(true);
                      } else {
                        handleToggleActive();
                      }
                    }}
                    disabled={updateCTA.isPending}
                  >
                    {cta.is_active ? "Deactivate" : "Activate"}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    icon={TrashIcon}
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Deactivate Confirmation Modal */}
      <ConfirmDialog
        isOpen={showDeactivateConfirm}
        onClose={() => setShowDeactivateConfirm(false)}
        onConfirm={handleToggleActive}
        title="Deactivate Call to Action"
        message={`Are you sure you want to deactivate "${cta.name}"? It will no longer appear in facility ID lookups or generate new vouchers. You can reactivate it later.`}
        confirmText="Deactivate"
        variant="danger"
        isLoading={updateCTA.isPending}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Call to Action"
        message={`Are you sure you want to delete "${cta.name}"? This will also delete any associated QR codes. This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteCTA.isPending}
      />
    </>
  );
};

export default OrganizationCTACard;
